import React, { useState, useEffect } from 'react';
import Interests from '../components/pages/Favorites/Interests';
import { useDispatch, useSelector } from 'react-redux';
import { refreshFavs, refreshInterestedV2, markAsReadInterested, refreshInterested } from '../actions/favActions';
import { refreshAccount } from '../actions/profileActions';
import { withPusher } from '../contexts/PusherContext';
import { PUSHER, SliderSourceLSKey } from '../config/constants';
import { useStateCallback } from '../utils/customHooks';
import { clearGlobalNotifications, getGlobalNotifications } from '../actions/globalNotificationActions';
import { getLocalItem } from '../common';
import { useLocation } from 'react-router-dom';
import { RootState } from '../store';
import useMediaBreakpoints from '../hooks/useMediaBreakpoints';
import MasterConfig from 'config/Master';

interface InterestsContainerProps {
  bindListener: any;
  stopListening: any;
  onRealtimeEvent: any;
}

const InterestsContainer = (props: InterestsContainerProps) => {
  const { bindListener, stopListening, onRealtimeEvent } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const fav = useSelector((state: RootState) => state.fav);
  const userProfile = useSelector((state: RootState) => state.profile);
  const { isMobile } = useMediaBreakpoints();

  const getActivePage = () => {
    let activePage = 'viewed_me';

    switch (location.pathname) {
      case '/interests':
        activePage = 'viewed_me';
        break;
      case '/interests/my-favorites':
        activePage = 'from';
        break;
      case '/interests/favorited-me':
        activePage = 'to';
        break;
    }

    return activePage;
  };
  const [page, setPage] = useStateCallback(getActivePage());
  const [prevLocation, setPrevLocation] = useState(location);
  const [prevHashId, setPrevHashId] = useState(userProfile.hash_id);

  useEffect(() => {
    bindListener && bindListener(listen);

    // check if favorites from or to is empty
    if (fav[page].length < 1) refreshEmptyFavs(page);
    return () => {
      stopListening && stopListening(listen);
      dispatch(clearGlobalNotifications());
      dispatch(getGlobalNotifications());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname !== prevLocation.pathname) {
      const prevPage = page;

      setPrevLocation(location);
      setPage(getActivePage(), newState => {
        if ((newState && newState !== prevPage) || userProfile.hash_id !== prevHashId) {
          setPrevHashId(userProfile.hash_id);
          refreshEmptyFavs(newState);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    const storageHashIds = JSON.parse(getLocalItem(SliderSourceLSKey.INTERESTS_LS)) || [];

    if (storageHashIds.length > 0) {
      dispatch(markAsReadInterested(storageHashIds));
    }
  }, []);

  const refreshInterestFav = () => {
    if (isMobile) {
      dispatch(refreshInterestedV2());
    } else {
      dispatch(refreshInterested());
    }

    dispatch(refreshAccount());
  };

  const refreshEmptyFavs = route => {
    const isViewedMe = route === 'viewed_me';
    const shouldRefreshTo = userProfile.has_fav_update > 0 || (route === 'to' && fav.to.length === 0);
    const shouldRefreshFrom = route === 'from' && fav.from.length === 0;

    if (!isViewedMe && (shouldRefreshTo || shouldRefreshFrom)) {
      // refresh data according to route.
      dispatch(refreshFavs(route, true));
      // also refresh account (for header counts).
      dispatch(refreshAccount());
    }

    if (isViewedMe && location.pathname === '/interests') {
      refreshInterestFav();
    }
  };

  const listen = () => {
    const { EVENTS } = PUSHER;

    onRealtimeEvent(EVENTS.ACCOUNT_ALERTS, data => {
      if (MasterConfig.APP_ENV !== 'production') {
        console.log(`Notification Received from Pusher for event - ${EVENTS.ACCOUNT_ALERTS}`, data);
      }

      if ((data.type === 'favorites' || data.type === 'offers') && data.action === 'receive') {
        if (page === 'viewed_me') {
          if (isMobile) {
            const storageHashIds = JSON.parse(getLocalItem(SliderSourceLSKey.INTERESTS_LS)) || [];

            if (storageHashIds.length > 0) {
              dispatch(markAsReadInterested(storageHashIds, false));
            } else {
              dispatch(refreshInterestedV2(false));
            }
          } else {
            dispatch(refreshInterested(true));
          }
        } else {
          dispatch(refreshFavs(page, false));
        }
      }
    });
  };

  return <Interests page={page} />;
};

export default withPusher(InterestsContainer);
